import styled, { css } from 'styled-components';

/**
 * The one look for "play this voice's sample" (PLAT-11581), in the two weights
 * the Creator Studio transport uses (`common/PlaybackControls.tsx`):
 *
 * - **Quiet** (default) — the forward/back buttons: a bare --text-secondary
 *   glyph that gains --surface-elevated and a --foreground glyph on hover. Used
 *   everywhere a sample sits beside other controls: the dub picker row, By
 *   Language rows, picker menu rows, the voice card's language rows. Even a
 *   single play next to a picker read too heavy in the filled weight.
 * - **Prominent** — the stage's primary play: a filled --foreground circle with
 *   a --background glyph. Reserved for a play that is the only control in its
 *   region and what the region is for: the Redub Selection popover's speaker
 *   row, whose play answers "what will the re-dub sound like" (PLAT-12042),
 *   while the passage's current-take play above it stays quiet.
 *
 * No green anywhere: green is the action colour, and a list of plays lit green
 * shouted over the list it sat in. Playing is carried by the pause glyph (and,
 * in the quiet weight, the hover surface staying on).
 */
export interface SamplePlayProps {
  $size?: number;
  $playing?: boolean;
  $prominent?: boolean;
}

const quietRest = css`
  background: transparent;
  color: var(--text-secondary);
`;
const quietLit = css`
  background: var(--surface-elevated);
  color: var(--foreground);
`;
const prominentRest = css`
  background: var(--foreground);
  color: var(--background);
`;

export const samplePlayCss = css<SamplePlayProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => p.$size ?? 28}px;
  height: ${p => p.$size ?? 28}px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  padding: 0;
  line-height: 1;
  transition: all 150ms ease-out;
  ${p => (p.$prominent ? prominentRest : p.$playing ? quietLit : quietRest)}
`;

/** Interactive form: a real button (dub picker rows, By Language rows). */
export const SamplePlayButton = styled.button<SamplePlayProps>`
  ${samplePlayCss}
  cursor: pointer;

  &:hover:not(:disabled) {
    ${p => (p.$prominent ? 'transform: scale(1.05);' : quietLit)}
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:disabled {
    cursor: not-allowed;
    ${p =>
      p.$prominent
        ? css`
            background: var(--surface-elevated);
            color: var(--text-muted);
          `
        : css`
            color: var(--text-muted);
            opacity: 0.4;
          `}
  }
`;

/**
 * Decorative form for a row that is itself the button (the voice card's
 * language rows): the row's hover drives the highlight via
 * \`\${SamplePlayGlyph}\`. Always the quiet weight.
 */
export const SamplePlayGlyph = styled.span<
  SamplePlayProps & { $disabled?: boolean }
>`
  ${samplePlayCss}
  ${p =>
    p.$disabled &&
    css`
      color: var(--text-muted);
      opacity: 0.4;
    `}
`;

/** The "no sample" placeholder, sized like the button so rows stay aligned. */
export const SampleUnavailable = styled.span<{ $size?: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => p.$size ?? 28}px;
  height: ${p => p.$size ?? 28}px;
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;
